import { WARS_LIMITS, isWarsRoomCode } from '@slimez/protocol';
import { parseAppMode } from './appMode';

/**
 * Which page this is (ADR-040, ADR-037; docs/14-SLIME-WARS/TECH.md §10). Production and
 * development alike:
 *
 *   (no query)                       the front page: a mode picker (`ui/home/`), neither game
 *   ?game=wars                       Slime Wars (its lazily loaded app under `src/wars/`)
 *   ?room=<7-char code>              a Slime Wars share link (TECH §8 code alphabet)
 *   ?game=preserve[&room=<code>]     Survival, the Preserve (lazily loaded; its hidden link and
 *                                    its share links — nothing on the site links to it)
 *   anything else                    the front page; a bare `?room=` that is not a Slime Wars code
 *                                    (an old Preserve share link) adds a short notice there
 *
 * DEV builds only: the Preserve's developer surfaces (`?spike`, `?hud=fixture`, `?shell=fixture`,
 * `?sandbox=1`, `?devpanel=1`; `appMode.ts`) still mount the Preserve app without `game=`.
 *
 * A code's length tells the games apart; the shape check is routing only, never authorization.
 * Switching pages is always a full page navigation (`location.assign` / a plain link), so the
 * Preserve runtime, the Slime Wars app and the front page never share a page.
 */
export type GameRoute = 'home' | 'preserve' | 'wars';

export const GAME_PARAM = 'game';
export const WARS_GAME_PARAM = 'wars';
export const PRESERVE_GAME_PARAM = 'preserve';

export function parseGameRoute(search: string, dev = false): GameRoute {
  const params = new URLSearchParams(search);
  const game = params.get(GAME_PARAM);
  if (game === WARS_GAME_PARAM) return 'wars';
  if (game === PRESERVE_GAME_PARAM) return 'preserve';
  const room = params.get('room');
  if (room !== null && isWarsRoomCode(room)) return 'wars';
  if (dev && parseAppMode(search, true) !== 'game') return 'preserve';
  return 'home';
}

/** This page's route (read once at load). */
export function resolveGameRoute(): GameRoute {
  if (typeof location === 'undefined') return 'home';
  return parseGameRoute(location.search, import.meta.env.DEV);
}

/**
 * W059: the privacy page's address (`/privacy`, a trailing slash tolerated). Its own page, by
 * PATH (Google's consent screen links to `https://<origin>/privacy`; the proxy's single-page
 * fallback serves the app there), whatever the query.
 */
export const PRIVACY_PATH = '/privacy';

export function isPrivacyPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === PRIVACY_PATH;
}

/** Whether this page is the privacy page (read once at load). */
export function resolvePrivacyPage(): boolean {
  if (typeof location === 'undefined') return false;
  return isPrivacyPath(location.pathname);
}

/** What the front page tells a visitor who arrived by a link it cannot honour. */
export type HomeNotice = 'SURVIVAL_CLOSED';

/**
 * The front page's notice for this query: a bare `?room=<code>` that is not a Slime Wars code is
 * an old Survival share link (ADR-040: Survival is "Coming soon"). `null` otherwise, and on any
 * page that is not the front page.
 */
export function parseHomeNotice(search: string, dev = false): HomeNotice | null {
  if (parseGameRoute(search, dev) !== 'home') return null;
  const room = new URLSearchParams(search).get('room');
  return room !== null && room.trim().length > 0 ? 'SURVIVAL_CLOSED' : null;
}

/** This page's front-page notice (read once at load). */
export function resolveHomeNotice(): HomeNotice | null {
  if (typeof location === 'undefined') return null;
  return parseHomeNotice(location.search, import.meta.env.DEV);
}

/** A typed Slime Wars code, tolerant of case and stray separators (`k7qx-2pm`). */
function normalizeWarsCode(text: string): string | undefined {
  const compact = text.replace(/[\s-]+/g, '').toUpperCase();
  return compact.length === WARS_LIMITS.roomCodeLength && isWarsRoomCode(compact)
    ? compact
    : undefined;
}

/**
 * The Slime Wars room code in what a player typed into a "join" field: the code itself or a share
 * link carrying `?room=<code>`. `undefined` when the text is not a Slime Wars code (a Preserve
 * code, a Preserve link, garbage), so the caller keeps its own handling.
 */
export function warsCodeFromInput(text: string): string | undefined {
  const trimmed = text.trim();
  if (trimmed.length === 0) return undefined;
  const queryAt = trimmed.indexOf('?');
  if (queryAt < 0) return normalizeWarsCode(trimmed);
  const room = new URLSearchParams(trimmed.slice(queryAt).split('#')[0]).get('room');
  return room === null ? undefined : normalizeWarsCode(room);
}

/**
 * Relative address of the Slime Wars app: a lobby's share target (`?room=<code>`) or the Slime
 * Wars entry (`?game=wars`). Relative to the current path, so it works under any base path.
 */
export function warsHref(code?: string): string {
  return code === undefined ? `?game=${WARS_GAME_PARAM}` : `?room=${encodeURIComponent(code)}`;
}

/**
 * Relative address of Survival (the Preserve): its entry (`?game=preserve`) or a lobby's share
 * target (`?game=preserve&room=<code>`). Every link the Preserve makes carries `game=preserve`,
 * so its hidden mode keeps working end to end (ADR-040).
 */
export function preserveHref(code?: string): string {
  const entry = `?${GAME_PARAM}=${PRESERVE_GAME_PARAM}`;
  return code === undefined ? entry : `${entry}&room=${encodeURIComponent(code)}`;
}

/** The front page (the mode picker): this page's path without a query. */
export function homeHref(): string {
  return typeof location === 'undefined' ? '/' : location.pathname;
}
